import { useEffect, startTransition, useMemo } from 'react';

import useLogRocketIdentify from '@/hooks/useLogRocketIdentify';
import useSession from '@/hooks/context/useSession';
import useRouter from '@/hooks/useRouter';
import Logger from '@/lib/logger';

export const ROLES = {
  admin: 'api.admin',
  siteManager: 'api.siteManager',
  support: 'api.support',
  report: 'api.report',
} as const;

export type RoleType = (typeof ROLES)[keyof typeof ROLES];

function useNeedRole(
  neededRoles: RoleType | RoleType[],
  redirectOnFail = true
) {
  const { roles } = useSession();
  const router = useRouter();

  // Compute synchronously during render. `roles` comes from the session
  // context (parsed from the JWT) and is available on the first render, so
  // there's no need to defer this to an effect — doing so would make
  // authorized users briefly see `false` and flash a loading/redirect state.
  const result = useMemo(() => {
    const currentNeededRoles =
      typeof neededRoles === 'string' ? [neededRoles] : neededRoles;

    return currentNeededRoles.some((role) => roles.includes(role));
  }, [neededRoles, roles]);

  useEffect(() => {
    if (!result && redirectOnFail) {
      const error = new Error('User does not have the needed role');
      Logger.warn('User does not have the needed role', {
        neededRoles,
        roles,
        redirectOnFail,
        error: error.stack,
      });
      startTransition(() => {
        router.redirectToLogin(false);
      });
    }
  }, [neededRoles, redirectOnFail, result, roles, router]);

  useLogRocketIdentify();

  return result;
}

export default useNeedRole;
